// Range-preset and granularity-rule helpers for the Engineering Insights
// toolbar. Dependency-free (no "@/..." imports) so the root vitest suite can
// import this module directly — same precedent as ./chart-data.ts.

export type RangePreset = "1m" | "3m" | "6m" | "12m" | "ytd" | "custom";

export interface DateRange {
	from: string;
	to: string;
}

const isoDate = (date: Date): string => date.toISOString().slice(0, 10);

export const rangeForPreset = (
	preset: Exclude<RangePreset, "custom">,
	now = new Date()
): DateRange => {
	const to = isoDate(now);
	if (preset === "ytd") {
		return { from: `${now.getUTCFullYear()}-01-01`, to };
	}
	const months = preset === "1m" ? 1 : preset === "3m" ? 3 : preset === "6m" ? 6 : 12;
	const from = new Date(now);
	from.setUTCDate(1);
	from.setUTCMonth(from.getUTCMonth() - months);
	return { from: isoDate(from), to };
};

const DAY_MS = 86_400_000;

export const rangeSpanDays = (from: string, to: string): number =>
	Math.round((Date.parse(to) - Date.parse(from)) / DAY_MS);

/** Ranges shorter than this produce too few monthly buckets to chart. */
export const AUTO_WEEKLY_MAX_DAYS = 62;

/**
 * One-shot granularity rule fired on every range change: short ranges switch
 * the dashboard to weekly; longer ranges leave the user's choice alone (null).
 * There is no daily granularity, so weekly is the floor for any short range.
 */
export const autoGranularityForRange = (from: string, to: string): "week" | null =>
	rangeSpanDays(from, to) < AUTO_WEEKLY_MAX_DAYS ? "week" : null;

const pad = (value: number): string => String(value).padStart(2, "0");

/** Local-component ISO date. DayPicker returns local-midnight Dates, so a
 * toISOString() slice would shift a day back in UTC+ timezones. */
export const dateToIsoLocal = (date: Date): string =>
	`${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/** Inverse of dateToIsoLocal — local-midnight Date for seeding DayPicker. */
export const isoToLocalDate = (iso: string): Date => {
	const [year, month, day] = iso.split("-").map(Number);
	return new Date(year, (month || 1) - 1, day || 1);
};
